// src/whatsappChat/components/TemplateForm.jsx
import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useWaApprovedTemplates } from '../api/hooks';
import { normalizeError } from '../api/client';
import { fillTemplate, isHttpsUrl, templateRequirements } from '../api/content';
import { Field } from './Modal';

/**
 * Pick an approved template of one business number and fill it in.
 * Calls onSend with the template fields of POST /messages; the caller adds
 * the address (conversation_id, or phone + number_id).
 */
export function TemplateForm({ numberId, sending, onSend, onCancel, submitLabel = 'Send template' }) {
  const { data: templates = [], isLoading, isError, error } = useWaApprovedTemplates(numberId);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [bodyVars, setBodyVars] = useState([]);
  const [headerLink, setHeaderLink] = useState('');
  const [buttonValues, setButtonValues] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? templates.filter((t) => String(t.name || '').toLowerCase().includes(q)) : templates;
  }, [templates, search]);

  const selected = templates.find((t) => t.id === selectedId) || null;
  const needs = useMemo(() => (selected ? templateRequirements(selected) : null), [selected]);

  const pick = (tpl) => {
    setSelectedId(tpl.id);
    setBodyVars(Array(templateRequirements(tpl).bodyCount).fill(''));
    setHeaderLink('');
    setButtonValues({});
    setShowErrors(false);
  };

  const errors = {};
  if (needs) {
    bodyVars.forEach((value, i) => {
      if (!String(value).trim()) errors[`body-${i}`] = 'Required';
    });
    if (needs.headerMedia && !isHttpsUrl(headerLink)) errors.header = 'Enter a public https link';
    needs.buttons.forEach((b) => {
      if (!String(buttonValues[b.index] || '').trim()) errors[`button-${b.index}`] = 'Required';
    });
  }
  const valid = Boolean(selected && needs?.supported && Object.keys(errors).length === 0);

  const submit = (e) => {
    e.preventDefault();
    if (sending || !selected) return;
    if (!valid) {
      setShowErrors(true);
      return;
    }
    const params = {};
    if (needs.headerMedia) params.header = { type: needs.headerMedia, link: headerLink.trim() };
    if (needs.buttons.length) {
      params.buttons = needs.buttons.map((b) => ({
        index: b.index,
        sub_type: b.subType,
        value: String(buttonValues[b.index]).trim(),
      }));
    }
    onSend({
      type: 'template',
      template_name: selected.name,
      template_language: selected.language || 'en',
      body_variables: bodyVars.map((v) => String(v).trim()),
      ...(Object.keys(params).length ? { template_params: params } : {}),
    });
  };

  const err = (key) => (showErrors ? errors[key] : null);

  return (
    <form className="wa-tpl" onSubmit={submit}>
      <div className="wa-tpl-cols">
        <div className="wa-tpl-list">
          <label className="wa-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates"
              aria-label="Search templates"
            />
          </label>

          <div className="wa-tpl-items">
            {isLoading ? (
              <div className="wa-state">Loading templates…</div>
            ) : isError ? (
              <div className="wa-state">{normalizeError(error).message}</div>
            ) : visible.length === 0 ? (
              <div className="wa-state">
                {templates.length === 0 ? 'No approved templates on this number.' : 'No templates match.'}
              </div>
            ) : (
              visible.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  className={`wa-tpl-item${tpl.id === selectedId ? ' is-active' : ''}`}
                  onClick={() => pick(tpl)}
                >
                  <span className="wa-tpl-name">{tpl.name}</span>
                  <span className="wa-tpl-meta">
                    {[tpl.category, tpl.language].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="wa-tpl-detail">
          {!selected ? (
            <div className="wa-state">Choose a template to fill in.</div>
          ) : !needs.supported ? (
            <div className="wa-state">
              {selected.template_type} templates cannot be sent from the chat yet.
            </div>
          ) : (
            <>
              <div className="wa-tpl-preview">
                <span className="wa-msg-label">Preview</span>
                {selected.header && <div className="wa-msg-header">{selected.header}</div>}
                <div className="wa-msg-text">{fillTemplate(selected.body, bodyVars)}</div>
                {selected.footer && <div className="wa-msg-footer">{selected.footer}</div>}
                {Array.isArray(selected.buttons) && selected.buttons.length > 0 && (
                  <div className="wa-msg-buttons">
                    {selected.buttons.map((b, i) => (
                      <span key={i} className="wa-msg-button">{b?.text || b?.type}</span>
                    ))}
                  </div>
                )}
              </div>

              {needs.headerMedia && (
                <Field
                  label={`Header ${needs.headerMedia} link`}
                  hint="Public https link to the file"
                  error={err('header')}
                >
                  <input
                    className="wa-input"
                    type="url"
                    value={headerLink}
                    onChange={(e) => setHeaderLink(e.target.value)}
                    placeholder="https://"
                  />
                </Field>
              )}

              {bodyVars.map((value, i) => (
                <Field key={i} label={`Variable {{${i + 1}}}`} error={err(`body-${i}`)}>
                  <input
                    className="wa-input"
                    type="text"
                    value={value}
                    onChange={(e) =>
                      setBodyVars((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
                    }
                  />
                </Field>
              ))}

              {needs.buttons.map((b) => (
                <Field key={b.index} label={b.label} error={err(`button-${b.index}`)}>
                  <input
                    className="wa-input"
                    type="text"
                    value={buttonValues[b.index] || ''}
                    onChange={(e) => setButtonValues((prev) => ({ ...prev, [b.index]: e.target.value }))}
                  />
                </Field>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="wa-modal-actions">
        <button type="button" className="wa-btn wa-btn-secondary" onClick={onCancel} disabled={sending}>
          Cancel
        </button>
        <button
          type="submit"
          className="wa-btn wa-btn-primary"
          disabled={sending || !selected || !needs?.supported}
        >
          {sending ? 'Sending…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
